import { handleActions, createAction } from 'redux-actions';
import update from 'immutability-helper';
import mapActions from '@goodwaygroup/react-map-actions';

// Actions
export const actions = mapActions([
    'FETCH_DATA',
    'FETCH_DATA_SUCCESS',
    'FETCH_CREATIVES',
    'FETCH_CREATIVES_SUCCESS',
    'FETCH_CALLOUTS',
    'FETCH_CALLOUTS_SUCCESS',
    'FETCH_TARGETING_TACTICS',
    'FETCH_TARGETING_TACTICS_SUCCESS',
    'FETCH_PLACEMENTS',
    'FETCH_PLACEMENTS_SUCCESS',
    'FETCH_ACTIVITIES',
    'FETCH_ACTIVITIES_SUCCESS',
    'FETCH_CHANNELS',
    'FETCH_CHANNELS_SUCCESS',
    'TOGGLE_CHANNEL_EXPANSION',
    'EXPAND_ALL_CHANNELS',
    'RESET_TO_INITIAL_STATE',
], 'SITE_DIRECT');

export const fetchIOChannel = createAction(actions.FETCH_DATA, params => params);
export const fetchIOChannelSuccess = createAction(actions.FETCH_DATA_SUCCESS, data => data);
export const fetchIOChannelCreatives = createAction(actions.FETCH_CREATIVES, params => params);
export const fetchIOChannelCreativesSuccess = createAction(actions.FETCH_CREATIVES_SUCCESS, data => data);
export const fetchIOChannelTargetingTactics = createAction(actions.FETCH_TARGETING_TACTICS, params => params);
export const fetchIOChannelTargetingTacticsSuccess =
    createAction(actions.FETCH_TARGETING_TACTICS_SUCCESS, data => data);
export const fetchIOChannelCallouts = createAction(actions.FETCH_CALLOUTS, params => params);
export const fetchIOChannelCalloutsSuccess = createAction(actions.FETCH_CALLOUTS_SUCCESS, data => data);
export const fetchIOChannelPlacements = createAction(actions.FETCH_PLACEMENTS, data => data);
export const fetchIOChannelPlacementsSuccess = createAction(actions.FETCH_PLACEMENTS_SUCCESS, data => data);
export const toggleChannelExpansion = createAction(actions.TOGGLE_CHANNEL_EXPANSION, channel => channel);
export const fetchIOChannelActivities = createAction(actions.FETCH_ACTIVITIES, params => params);
export const fetchIOChannelActivitiesSuccess = createAction(actions.FETCH_ACTIVITIES_SUCCESS, data => data);
export const fetchIOChannels = createAction(actions.FETCH_CHANNELS);
export const fetchIOChannelsSuccess = createAction(actions.FETCH_CHANNELS_SUCCESS, data => data);

export const resetSiteDirectInitialState = createAction(actions.RESET_TO_INITIAL_STATE);
export const expandAllChannels = createAction(actions.EXPAND_ALL_CHANNELS);

// Reducers
const channelInitialState = {
    callouts: null,
    creatives: null,
    targetingTactics: null,
    data: null,
    xAxisLabel: null,
    statistics: null,
    categories: null,
    apiConfig: null,
    activities: null,
    placements: null,
    channel: null,
    hasData: false,
    expand: {
        hide: true,
        initialExpand: false
    }
};

const initialState = {
    channels: {
        audio: channelInitialState,
        display: channelInitialState,
        mobile: channelInitialState,
        native: channelInitialState,
        video: channelInitialState
    },
    meta: {
        channels: [],
        hasData: null
    }
};

export default handleActions({
    [actions.FETCH_DATA_SUCCESS]: (state, action) => {
        const { hasData, ioChannel } = action.payload.params;
        if (hasData) {
            const rootState = action.getState();
            const scopeKey = action.payload.categories.config.selector;

            const updatedData = {
                hasData,
                channel: action.payload.params.ioChannel,
                data: action.payload[scopeKey],
                statistics: action.payload.statistics,
                categories: action.payload.categories[scopeKey],
                xAxisLabel: action.payload.categories.config.xAxisLabel,
                apiConfig: rootState.config.selectedTimeRange.apiConfig,
            };

            return update(state, {
                // $merging because $set clobbers callouts
                // that are generally requested at nearly the same time
                channels: { [ioChannel]: { $merge: updatedData } }
            });
        }

        const updatedData = Object.assign({}, channelInitialState);
        updatedData.hasData = hasData;
        updatedData.channel = action.payload.params.ioChannel;
        return update(state, {
            // $merging because $set clobbers callouts
            // that are generally requested at nearly the same time
            channels: { [ioChannel]: { $merge: updatedData } }
        });
    },

    [actions.FETCH_CREATIVES_SUCCESS]: (state, action) => update(state, {
        channels: {
            [action.payload.params.ioChannel]: {
                $merge: {
                    creatives: action.payload
                }
            }
        }
    }),

    [actions.RESET_TO_INITIAL_STATE]: state => update(state, { $set: initialState }),

    [actions.FETCH_CALLOUTS_SUCCESS]: (state, action) => update(state, {
        channels: {
            [action.payload.params.ioChannel]: {
                $merge: {
                    callouts: action.payload
                }
            }
        }
    }),

    [actions.FETCH_TARGETING_TACTICS_SUCCESS]: (state, action) => update(state, {
        channels: {
            [action.payload.params.ioChannel]: {
                $merge: {
                    targetingTactics: action.payload
                }
            }
        }
    }),

    [actions.FETCH_PLACEMENTS_SUCCESS]: (state, action) => update(state, {
        channels: {
            [action.payload.params.ioChannel]: {
                $merge: {
                    placements: action.payload
                }
            }
        }
    }),

    [actions.FETCH_ACTIVITIES_SUCCESS]: (state, action) => {
        const hasData = action.payload.params.hasData;

        if (hasData) {
            const rootState = action.getState();
            const scopeKey = action.payload.categories.config.selector;
            const data = {};
            action.payload.activities.forEach((activity) => {
                data[activity] = {
                    statistics: action.payload[activity].statistics,
                    items: action.payload[activity][scopeKey]
                };
            });

            const updatedData = {
                keys: action.payload.activities,
                hasData,
                data,
                rankings: action.payload.rankings,
                categories: action.payload.categories[scopeKey],
                xAxisLabel: action.payload.categories.config.xAxisLabel,
                apiConfig: rootState.config.selectedTimeRange.apiConfig
            };

            return update(state, {
                channels: {
                    [action.payload.params.ioChannel]: {
                        $merge: {
                            activities: updatedData
                        }
                    }
                }
            });
        }

        const updatedData = {
            hasData
        };

        return update(state, {
            channels: {
                [action.payload.params.ioChannel]: {
                    $merge: {
                        activities: updatedData
                    }
                }
            }
        });
    },

    [actions.FETCH_CHANNELS_SUCCESS]: (state, action) => {
        const hasData = action.payload.params.hasData;

        if (hasData) {
            // All channels have the same set of mertics returned.
            const metricKeys = Object.keys(action.payload[action.payload.channels[0]].statistics);

            const scopeKey = action.payload.categories.config.selector;
            const data = {};
            const groupedData = {};
            const treeMapData = {};
            const insights = metricKeys.reduce((prev, key) => {
                prev.percentages[key] = {};
                prev.totals[key] = 0;
                return prev;
            }, { percentages: {}, totals: {} });

            action.payload.channels.forEach((channel) => {
                data[channel] = {
                    statistics: { channel, ...action.payload[channel].statistics },
                    items: action.payload[channel][scopeKey]
                };
                treeMapData[channel] = action.payload[channel].statistics;
                groupedData[channel] = action.payload[channel][scopeKey];
                metricKeys.forEach((key) => {
                    insights.totals[key] += action.payload[channel].statistics[key];
                });
            });

            // Generate percentages
            action.payload.channels.forEach((channel) => {
                const stats = data[channel].statistics;
                const totals = insights.totals;

                Object.keys(insights.percentages).forEach((metric) => {
                    insights.percentages[metric][channel] = (stats[metric] / totals[metric]) * 100;
                });
            });

            const updatedData = {
                meta: {
                    channels: { $set: action.payload.channels },
                    data: { $set: data },
                    treeMapData: { $set: treeMapData },
                    groupedData: { $set: groupedData },
                    insights: { $set: insights },
                    rankings: { $set: action.payload.rankings },
                    hasData: { $set: hasData }
                }
            };

            return update(state, updatedData);
        }

        return update(state, { meta: { hasData: { $set: hasData } } });
    },

    [actions.TOGGLE_CHANNEL_EXPANSION]: (state, action) => update(state, {
        channels: {
            [action.payload]: {
                expand: {
                    initialExpand: { $set: true },
                    $toggle: ['hide']
                }
            }
        }
    }),

    [actions.EXPAND_ALL_CHANNELS]: state => update(state, {
        channels: Object.keys(initialState.channels).reduce((prev, key) => {
            prev[key] = {
                expand: {
                    initialExpand: { $set: true },
                    hide: { $set: false }
                }
            };
            return prev;
        }, {})
    })
}, initialState);



// WEBPACK FOOTER //
// ./src/services/siteDirect/duck.js